<script lang="ts">
  // BJL-CHECK: Card (used), Badge (used, arrival chips), Spinner (used), Tag, Button. The card
  // and every arrival chip come from bjl; the route roundels stay custom because circle-vs-diamond
  // is the MTA local/express distinction, i.e. data. The card-wide open target is a bare custom
  // <button>: a bjl Button carries its own chrome/sizing and cannot be a transparent full-bleed
  // overlay sitting behind the ✕. See artifacts/deviations/FavoritesPage.md.
  //
  // Compact arrivals card for a single favorited subway station.
  import { Spinner } from '@byjackli-labs/ui/Spinner'
  import { Colors, compareRoutes, routeBadgeLabel } from '../../ui/colors'
  import { Type } from '../../ui/theme'
  import { useFavoritesStore } from '../../store/favorites'
  import { api, type StopArrival } from '../../services/api'
  import { usePollingResource } from '../../lib/polling.svelte'
  import { storeSelect } from '../../lib/store.svelte'
  import { formatCountdown } from '../../ui/transitTime'
  import FavoriteArrivalCard from './FavoriteArrivalCard.svelte'
  import RouteBadge from '../../components/RouteBadge.svelte'

  interface Props {
    stopId: string
    stopName: string
    onRemove: () => void
    onOpen: () => void
  }

  const { stopId, stopName, onRemove, onOpen }: Props = $props()

  const favoriteRoutes = storeSelect(useFavoritesStore, (s) => s.favoriteRoutes)

  let arrivals = $state<StopArrival[] | null>(null)
  let loading = $state(true)

  usePollingResource(() => stopId, {
    intervalMs: 30_000,
    load: async (id) => (await api.getStopArrivals(id)).arrivals,
    onStart: () => { loading = true },
    onSuccess: (nextArrivals) => {
      arrivals = nextArrivals
      loading = false
    },
    onError: () => {
      arrivals = []
      loading = false
    },
  })

  // Every route calling here, favorited ones first, representing a station's identity on the card.
  const routeIds = $derived.by(() => {
    const ids = [...new Set((arrivals ?? []).map((a) => a.routeId))]
    return ids.sort((a, b) => {
      const af = favoriteRoutes.value.has(a)
      const bf = favoriteRoutes.value.has(b)
      if (af !== bf) return af ? -1 : 1
      return compareRoutes(a, b)
    })
  })

  // The one arrival the card reports: soonest first, but a favorited line wins a tie-break
  // against any train that isn't more than a minute earlier. A rider who starred the R at a
  // station served by the N, Q, R and W wants to know about the R when an N leaving thirty
  // seconds sooner is not the answer they came for.
  const FAVORITE_GRACE_S = 60

  const next = $derived.by(() => {
    if (!arrivals || arrivals.length === 0) return null
    let best: { time: number; routeId: string; direction: string } | null = null
    for (const a of arrivals) {
      const t = a.times[0]
      if (!t) continue
      const fav = favoriteRoutes.value.has(a.routeId)
      const bestFav = best ? favoriteRoutes.value.has(best.routeId) : false
      if (
        !best ||
        (fav && !bestFav && t.time <= best.time + FAVORITE_GRACE_S) ||
        (fav === bestFav && t.time < best.time)
      ) {
        best = { time: t.time, routeId: a.routeId, direction: a.direction }
      }
    }
    if (!best) return null
    const label = formatCountdown(best.time, { includeUnit: false })
    // NYC subway: N = Uptown/inbound, S = Downtown/outbound; anything else has no
    // rider-facing name, so the line id stands alone.
    const dir = best.direction === 'N' ? 'uptown' : best.direction === 'S' ? 'downtown' : ''
    return {
      label,
      isNow: label === 'Now',
      detail: dir ? `${routeBadgeLabel(best.routeId)} ${dir}` : routeBadgeLabel(best.routeId),
    }
  })
</script>

{#snippet badges()}
  {#each routeIds as routeId (routeId)}
    <div style="position: relative; flex-shrink: 0;">
      <RouteBadge {routeId} size={22} />
      {#if favoriteRoutes.value.has(routeId)}
        <span
          style="
            position: absolute; bottom: -2px; right: -3px;
            font-size: 10px; line-height: 1; pointer-events: none;
            color: {Colors.warning}; text-shadow: 0 0 3px rgba(0,0,0,0.9);
          "
        >★</span>
      {/if}
    </div>
  {/each}
{/snippet}

{#snippet trailing()}
  {#if loading}
    <Spinner size="sm" label="Loading arrivals" />
  {:else if next}
    <div>
      <span style="color: {next.isNow ? Colors.success : Colors.text}; {Type.screen};">
        {next.label}
      </span>
      {#if !next.isNow}
        <span style="color: {Colors.textSecondary}; {Type.caption};"> min</span>
      {/if}
    </div>
    <div style="color: {Colors.textMuted}; {Type.caption}; margin-top: 2px;">
      {next.detail}
    </div>
  {:else}
    <div style="color: {Colors.textMuted}; {Type.caption};">No arrivals</div>
  {/if}
{/snippet}

<FavoriteArrivalCard
  title={stopName}
  {onRemove}
  {onOpen}
  {badges}
  {trailing}
/>
